import { fill, messages, type Lang } from "../i18n/index";

/**
 * What the browser says about the viewport, in CSS pixels. iOS home-screen
 * apps disagree with themselves here, so the stats readout shows all of it.
 */
export interface ViewportInfo {
  width: number;
  height: number;
  visualHeight: number;
  screenWidth: number;
  screenHeight: number;
  safeTop: number;
  safeRight: number;
  safeBottom: number;
  safeLeft: number;
  /** What `100vh`, `100lvh`, `100dvh` and `100svh` measure, and the body's height. */
  vh: number;
  lvh: number;
  dvh: number;
  svh: number;
  body: number;
  /** Where the scene canvas ends. */
  canvasBottom: number;
  standalone: boolean;
}

export function formatViewport(v: ViewportInfo, lang: Lang): string {
  const { viewport } = messages(lang);
  const r = Math.round;
  return fill(viewport.lines, {
    width: r(v.width),
    height: r(v.height),
    visual: r(v.visualHeight),
    screenWidth: r(v.screenWidth),
    screenHeight: r(v.screenHeight),
    top: r(v.safeTop),
    right: r(v.safeRight),
    bottom: r(v.safeBottom),
    left: r(v.safeLeft),
    mode: v.standalone ? viewport.installed : viewport.browser,
    vh: r(v.vh),
    lvh: r(v.lvh),
    dvh: r(v.dvh),
    svh: r(v.svh),
    body: r(v.body),
    canvas: r(v.canvasBottom),
  });
}

/** Invisible elements sized by each viewport unit and padded by the safe area. */
export function mountViewportProbe(
  container: HTMLElement,
  canvas: HTMLElement,
): { measure(): ViewportInfo } {
  const probe = (css: string): HTMLDivElement => {
    const el = document.createElement("div");
    el.style.cssText = `position:fixed;top:0;left:0;width:0;visibility:hidden;pointer-events:none;${css}`;
    el.setAttribute("aria-hidden", "true");
    container.append(el);
    return el;
  };
  const units = (["vh", "lvh", "dvh", "svh"] as const).map(
    (unit) => [unit, probe(`height:100${unit}`)] as const,
  );
  const safe = probe(
    "height:0;padding:env(safe-area-inset-top,0px) env(safe-area-inset-right,0px) env(safe-area-inset-bottom,0px) env(safe-area-inset-left,0px)",
  );
  return {
    measure() {
      const pad = getComputedStyle(safe);
      const px = (value: string) => parseFloat(value) || 0;
      const heights = Object.fromEntries(units.map(([unit, el]) => [unit, el.offsetHeight]));
      return {
        width: window.innerWidth,
        height: window.innerHeight,
        visualHeight: window.visualViewport?.height ?? window.innerHeight,
        screenWidth: screen.width,
        screenHeight: screen.height,
        safeTop: px(pad.paddingTop),
        safeRight: px(pad.paddingRight),
        safeBottom: px(pad.paddingBottom),
        safeLeft: px(pad.paddingLeft),
        vh: heights.vh,
        lvh: heights.lvh,
        dvh: heights.dvh,
        svh: heights.svh,
        body: document.body.getBoundingClientRect().height,
        canvasBottom: canvas.getBoundingClientRect().bottom,
        standalone: matchMedia("(display-mode: standalone)").matches,
      };
    },
  };
}
